import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

import { ReplayStep } from '../replaysApi';

const mono = {
    m: 0,
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
} as const;

/** What the agent did, in order: tool calls with their results, drafts, and system checks. */
export const ReplaySteps: React.FC<{ steps: ReplayStep[] }> = ({ steps }) => {
    if (steps.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No steps recorded.
            </Typography>
        );
    }
    return (
        <Box data-testid="replay-steps">
            {steps.map((step, index) => (
                <Box
                    key={index}
                    sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5, py: 0.5, mb: 1 }}
                >
                    {step.type === 'tool' && (
                        <>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                                    {step.name}
                                </Typography>
                                {step.stubbed ? (
                                    <Chip size="small" color="warning" label="dry run: not executed" />
                                ) : (
                                    <Chip size="small" variant="outlined" label="live read" />
                                )}
                                {step.forced && <Chip size="small" variant="outlined" label="forced by the graph" />}
                            </Box>
                            <Box component="pre" sx={mono}>
                                {JSON.stringify(step.args, null, 2)}
                            </Box>
                            {step.result !== null && (
                                <Box component="pre" sx={{ ...mono, color: 'text.secondary' }}>
                                    {step.result}
                                </Box>
                            )}
                        </>
                    )}
                    {step.type === 'reply' && (
                        <>
                            <Typography variant="caption" color="text.secondary">
                                Reply written
                            </Typography>
                            <Box component="pre" sx={mono}>
                                {step.content}
                            </Box>
                        </>
                    )}
                    {step.type === 'guard' && (
                        <>
                            <Chip size="small" color="info" label="system check sent to the model" />
                            <Box component="pre" sx={{ ...mono, color: 'text.secondary' }}>
                                {step.content}
                            </Box>
                        </>
                    )}
                </Box>
            ))}
        </Box>
    );
};
